import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Key, ArrowLeft, Store, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import MiniFooter from "../components/MiniFooter";

export default function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [searchParams] = useSearchParams();
  const presetRole = searchParams.get("role") === "seller" ? "seller" : "investor";
  const [role, setRole] = useState(presetRole);
  const [form, setForm] = useState({ full_name: "", email: "", password: "", phone: "" });
  const [accept, setAccept] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!accept) { toast.error("Debes aceptar los términos para continuar"); return; }
    setLoading(true);
    try {
      const u = await register({ ...form, role });
      toast.success("Cuenta creada");
      navigate(u.role === "seller" ? "/seller" : "/investor");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al registrar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col">
      <header className="px-6 py-5 border-b border-zinc-200 bg-white">
        <Link to="/" className="flex items-center w-fit" data-testid="register-logo">
          <img src="/logo-brand.png" alt="DerechoLlave" className="h-14 w-auto" />
        </Link>
      </header>
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-xl">
          <Link to="/" className="text-sm text-slate-500 inline-flex items-center gap-1 mb-6 hover:underline" data-testid="back-home">
            <ArrowLeft className="w-4 h-4" /> Volver al inicio
          </Link>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Crea tu cuenta</h1>
          <p className="text-slate-500 mt-2 text-sm">¿Qué tipo de cuenta deseas crear?</p>

          <div className="mt-6 grid grid-cols-2 gap-3">
            <button onClick={() => setRole("investor")} className={`border rounded-lg p-5 text-left transition ${role === "investor" ? "border-slate-900 bg-white shadow-sm" : "border-zinc-200 bg-zinc-50 hover:bg-white"}`} data-testid="role-investor">
              <TrendingUp className={`w-6 h-6 ${role === "investor" ? "text-emerald-600" : "text-slate-400"}`} />
              <p className="mt-3 font-semibold">Inversionista</p>
              <p className="text-xs text-slate-500">Busca y compra negocios funcionando</p>
            </button>
            <button onClick={() => setRole("seller")} className={`border rounded-lg p-5 text-left transition ${role === "seller" ? "border-slate-900 bg-white shadow-sm" : "border-zinc-200 bg-zinc-50 hover:bg-white"}`} data-testid="role-seller">
              <Store className={`w-6 h-6 ${role === "seller" ? "text-amber-600" : "text-slate-400"}`} />
              <p className="mt-3 font-semibold">Vendedor</p>
              <p className="text-xs text-slate-500">Publica y vende tu negocio</p>
            </button>
          </div>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="full_name">Nombre completo</Label>
              <Input id="full_name" required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="register-name" />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="register-email" />
            </div>
            <div>
              <Label htmlFor="phone">Teléfono (opcional)</Label>
              <Input id="phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="register-phone" />
            </div>
            <div>
              <Label htmlFor="password">Contraseña</Label>
              <Input id="password" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="register-password" />
            </div>
            <label className="flex items-start gap-3 p-4 border border-zinc-200 rounded-lg bg-zinc-50 cursor-pointer">
              <Checkbox checked={accept} onCheckedChange={setAccept} data-testid="register-terms-checkbox" className="mt-0.5" />
              <span className="text-xs text-slate-600 leading-relaxed">
                He leído y acepto los <Link to="/terminos" target="_blank" className="font-semibold text-slate-900 underline" data-testid="register-terms-link">Términos, condiciones y aviso de responsabilidad</Link>.
                Entiendo que DerechoLlave es una plataforma de intermediación, que no garantiza la
                veracidad de la información publicada ni el desempeño futuro de los negocios, y que
                la decisión de comprar o vender es de mi exclusiva responsabilidad.
              </span>
            </label>
            <Button type="submit" disabled={loading || !accept} className="w-full btn-primary h-11" data-testid="register-submit">
              {loading ? "Creando..." : "Crear cuenta"}
            </Button>
          </form>

          <p className="text-center text-sm text-slate-500 mt-6">
            ¿Ya tienes cuenta? <Link to="/login" className="font-semibold text-slate-900 hover:underline" data-testid="register-to-login">Inicia sesión</Link>
          </p>
        </div>
      </main>
    </div>
  );
}
